<script setup lang="ts">
const pageUrl = 'https://www.zenapptic.ai/workplace-of-the-future';
const eventImage = `${useCdnUrl()}/assets/tad/hero-bg.webp`;

useSeoMeta({
  title: 'Workplace of the Future Tad 2026 | Zenapptic AI',
  description: 'Register for Zenapptic AI’s Workplace of the Future event in New York and experience the future of work, technology, and personalized digital experiences.',
  robots: 'index, follow',
  ogTitle: 'Workplace of the Future Event | Zenapptic AI',
  ogDescription: 'Register for Zenapptic AI’s Workplace of the Future event in New York.',
  ogType: 'website',
  ogUrl: pageUrl,
  ogImage: eventImage,
  ogImageAlt: 'Workplace of the Future event at Zenapptic AI',
  twitterCard: 'summary_large_image',
  twitterTitle: 'Workplace of the Future Event | Zenapptic AI',
  twitterDescription: 'Register for Zenapptic AI’s Workplace of the Future event in New York.',
  twitterImage: eventImage,
  twitterImageAlt: 'Workplace of the Future event at Zenapptic AI',
});

useHead({
  link: [{rel: 'canonical', href: pageUrl}],
  script: [
    {
      key: 'workplace-of-the-future-event-schema',
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Event',
        name: 'Workplace of the Future',
        description: 'Experience the Workplace of the Future with Zenapptic AI, including personalized digital experiences and avatars.',
        startDate: '2026-10-14T14:00:00-04:00',
        endDate: '2026-10-14T19:00:00-04:00',
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        image: [eventImage],
        url: pageUrl,
        location: {
          '@type': 'Place',
          name: 'Zenapptic AI',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '246 W 38th St., 6th Floor',
            addressLocality: 'New York',
            addressRegion: 'NY',
            postalCode: '10018',
            addressCountry: 'US',
          },
        },
        organizer: {
          '@type': 'Organization',
          name: 'Zenapptic AI',
          url: 'https://www.zenapptic.ai',
        },
        offers: {
          '@type': 'Offer',
          url: pageUrl,
          price: '0',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
        },
      }),
    },
  ],
});

const route = useRoute();
const appMounted = useState('appMounted');
const isSubmitting = ref(false);
const submissionMessage = ref('');
const submissionError = ref(false);
const locationGroups = [
  {
    label: 'United States',
    options: [
      'Alabama',
      'Alaska',
      'Arizona',
      'Arkansas',
      'California',
      'Colorado',
      'Connecticut',
      'Delaware',
      'Florida',
      'Georgia',
      'Hawaii',
      'Idaho',
      'Illinois',
      'Indiana',
      'Iowa',
      'Kansas',
      'Kentucky',
      'Louisiana',
      'Maine',
      'Maryland',
      'Massachusetts',
      'Michigan',
      'Minnesota',
      'Mississippi',
      'Missouri',
      'Montana',
      'Nebraska',
      'Nevada',
      'New Hampshire',
      'New Jersey',
      'New Mexico',
      'New York',
      'North Carolina',
      'North Dakota',
      'Ohio',
      'Oklahoma',
      'Oregon',
      'Pennsylvania',
      'Rhode Island',
      'South Carolina',
      'South Dakota',
      'Tennessee',
      'Texas',
      'Utah',
      'Vermont',
      'Virginia',
      'Washington',
      'Washington, D.C.',
      'West Virginia',
      'Wisconsin',
      'Wyoming',
    ],
  },
  {
    label: 'Canada',
    options: ['Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan', 'Yukon'],
  },
];

function formatPhoneNumber(event: Event) {
  const input = event.target as HTMLInputElement;
  const digits = input.value.replace(/\D/g, '').slice(0, 10);

  if (digits.length > 6) {
    input.value = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else if (digits.length > 3) {
    input.value = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else if (digits.length > 0) {
    input.value = `(${digits}`;
  } else {
    input.value = '';
  }
}

async function submitRegistration(event: SubmitEvent) {
  const form = event.currentTarget as HTMLFormElement;
  const formData = new FormData(form);

  isSubmitting.value = true;
  submissionMessage.value = '';
  submissionError.value = false;

  try {
    await $fetch('/api/workplace-of-the-future/register', {
      method: 'POST',
      body: {
        name: `${formData.get('firstName')} ${formData.get('lastName')}`.trim(),
        email: formData.get('email'),
        company: formData.get('company'),
        title: formData.get('jobTitle'),
        phone: formData.get('phone'),
        industry: formData.get('industry'),
        stateProvince: formData.get('stateProvince'),
        visitTime: formData.get('visitTime'),
      },
    });

    form.reset();
    submissionMessage.value = 'Thank you — your registration has been submitted.';
  } catch (error: any) {
    const statusCode = error?.statusCode || error?.response?.status;

    submissionError.value = true;
    submissionMessage.value = statusCode === 409
      ? 'An attendee with this email is already registered.'
      : error?.data?.error
      || error?.data?.data?.error
      || error?.data?.message
      || error?.statusMessage
      || error?.message
      || 'Something went wrong. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
}

onMounted(() => {
  const {sendScreenView} = useAnalytics();

  if (appMounted.value) sendAnalytics();
  watch(appMounted, () => sendAnalytics());

  function sendAnalytics() {
    sendScreenView({type: 'PageView', target: `Solutions.${String(route.name)}`}, true);
  }
});
</script>

<template>
  <main class="container tad">
    <!-- Local hero block: customize or replace this section as needed. -->
    <div class="hero" :style="{backgroundImage: `url(${useCdnUrl()}/assets/tad/hero-bg.webp)`}">
      <div class="hero-content-container">
        <div class="hero-content">
          <div>
            <h3>
              <b>ZENAPPTIC</b>
              presents the
            </h3>
            <h1>
              <b>WORKPLACE</b>
              <br />
              OF THE
              <b>FUTURE</b>
            </h1>
          </div>
          <div class="tad-brand-row">
            <span class="tad-at"><i>@</i></span>
            <img class="tad-logo" :src="`${useCdnUrl()}/assets/tad/Tad.webp`" alt="TAD logo" />
          </div>
        </div>
      </div>
    </div>

    <section>
      <div>
        <h2>
          ABOUT THE
          <b>EVENT</b>
        </h2>

        <div class="flex">
          <div>
            <h3 class="event-date">
              <b>
                <span>WEDNESDAY, OCTOBER 14</span>
                <span class="event-time">2pm-7pm</span>
              </b>
            </h3>

            <p>Register now to explore new possibilities for the workplace and help shape what comes next.</p>
          </div>

          <div class="map">
            <p class="text-gray">
              246 W 38th St., 6th Floor,
              <br />
              New York, NY 10018
            </p>

            <a href="https://www.google.com/search?q=246+W+38th+St.%2C+6th+Floor%2C+New+York%2C+NY+10018&oq=246+W+38th+St.%2C+6th+Floor%2C+New+York%2C+NY+10018&gs_lcrp=EgZjaHJvbWUyBggAEEUYOTIICAEQABgWGB4yCggCEAAYogQYiQUyCggDEAAYgAQYogQyCggEEAAYgAQYogQyBwgFEAAY7wUyCggGEAAYgAQYogTSAQc2OTNqMGo0qAIBsAIB8QW-LclNCcRvoQ&sourceid=chrome&source=chrome.ob&ie=UTF-8" target="_blank" rel="noopener noreferrer" aria-label="Open event location in Google Maps">
              <img :src="`${useCdnUrl()}/assets/tad/map.webp`" alt="Map showing the event location" />
            </a>
          </div>
        </div>
      </div>
    </section>

    <div class="event-sections">
      <section class="event-partners">
        <h2>
          EVENT
          <b>PARTNERS</b>
        </h2>

        <div class="logos">
          <a href="https://www.nanolumens.com/" target="_blank"><img :src="`${useCdnUrl()}/assets/tad/NAnolumens.webp`" alt="Nanolumens logo" /></a>
          <a href="https://www.salamanderdesigns.com/" target="_blank"><img :src="`${useCdnUrl()}/assets/tad/Salamander.webp`" alt="Salamander logo" /></a>
          <a href="https://onetimessquare.com/" target="_blank"><img class="ots-logo" :src="`${useCdnUrl()}/assets/tad/ots-logo-black.webp`" alt="One Times Square logo" /></a>
        </div>
      </section>

      <section class="event-partners">
        <h2>
          EVENT
          <b>REGISTRATION</b>
        </h2>

        <div class="form">
          <form @submit.prevent="submitRegistration">
            <div class="form-row">
              <label>
                <span class="sr-only">First name</span>
                <input type="text" name="firstName" placeholder="First name *" autocomplete="given-name" required />
              </label>
              <label>
                <span class="sr-only">Last name</span>
                <input type="text" name="lastName" placeholder="Last name *" autocomplete="family-name" required />
              </label>
            </div>

            <div class="form-row single-field">
              <label>
                <span class="sr-only">Email</span>
                <input type="email" name="email" placeholder="Email *" autocomplete="email" required />
              </label>
            </div>

            <div class="form-row">
              <label>
                <span class="sr-only">Company name</span>
                <input type="text" name="company" placeholder="Company name *" autocomplete="organization" required />
              </label>
              <label>
                <span class="sr-only">Job title</span>
                <input type="text" name="jobTitle" placeholder="Job title" autocomplete="organization-title" />
              </label>
            </div>

            <div class="form-row single-field">
              <label>
                <span class="sr-only">Industry</span>
                <input type="text" name="industry" placeholder="Industry" />
              </label>
            </div>

            <div class="form-row">
              <label>
                <span class="sr-only">Phone number</span>
                <input type="tel" name="phone" placeholder="Phone number *" autocomplete="tel" inputmode="tel" maxlength="14" required @input="formatPhoneNumber" />
              </label>
              <label>
                <span class="sr-only">State / Province</span>
                <select name="stateProvince" autocomplete="address-level1" required>
                  <option value="" disabled selected>State / Province *</option>
                  <optgroup v-for="group in locationGroups" :key="group.label" :label="group.label">
                    <option v-for="option in group.options" :key="option" :value="option">{{ option }}</option>
                  </optgroup>
                  <option value="Other">Other</option>
                </select>
              </label>
            </div>

            <div class="form-row single-field">
              <label>
                <span class="sr-only">Approximately when do you plan to stop by?</span>
                <select name="visitTime" required>
                  <option value="" disabled selected>Approximately when do you plan to stop by? *</option>
                  <option value="early-afternoon">Early afternoon — 2:00pm - 3:30pm</option>
                  <option value="mid-afternoon">Mid afternoon — 3:30pm - 5:00pm</option>
                  <option value="late-afternoon-evening">Late afternoon/evening — 5:00pm - 7:00pm</option>
                </select>
              </label>
            </div>

            <p class="form-required-note">
              <span aria-hidden="true">*</span>
              Required fields
            </p>

            <button type="submit" :disabled="isSubmitting">
              {{ isSubmitting ? 'Submitting...' : 'Submit' }}
            </button>

            <p v-if="submissionMessage" class="submission-message" :class="{error: submissionError}" aria-live="polite">
              {{ submissionMessage }}
            </p>
          </form>
        </div>
      </section>
    </div>
  </main>
</template>

<style lang="scss">
main.tad {
  margin-top: 89px;
  background-color: black;
  display: flex;
  flex-direction: column;
  gap: 4rem;

  h2,
  //h3,
  p {
    color: white;
    font-weight: 100;

    b {
      font-weight: 700;
    }
  }

  h3 {
    color: white;
    font-weight: 100;
    font-size: min(7vw, 3rem);

    b {
      font-weight: 700;
    }
  }

  .hero {
    align-items: center;
    background-position: center;
    background-size: cover;
    display: flex;
    height: calc(100vh - 89px);
    justify-content: center;
    max-height: 540px;
    overflow: hidden;
    width: 100%;

    .hero-content-container {
      align-items: center;
      display: flex;
      height: 100%;
      justify-content: flex-start;
      max-width: 1366px;
      margin: auto;
      padding: 2rem;

      width: 100%;

      .hero-content {
        max-width: 1366px;
        width: max-content;

        .tad-brand-row {
          align-items: center;
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          margin-top: 1rem;
        }

        h1,
        h3 {
          margin-bottom: 0;
          margin-top: 0;
          font-weight: 100;

          b {
            font-weight: 700;
          }
        }

        .tad-at {
          color: white;
          display: inline-block;
          font-size: clamp(3rem, 5vw, 5rem);
          font-weight: 300;
          font-style: italic;
          line-height: 1;
          transform: skewX(-12deg);
        }

        .tad-logo {
          display: block;
          filter: drop-shadow(0 0 6px rgba(0, 0, 0, 0.95)) drop-shadow(0 0 14px rgba(0, 0, 0, 0.8));
          height: auto;
          width: 153px;
        }

        h1 {
          color: white;

          font-size: min(10vw, 7.2rem) !important;
          font-weight: 100;
          line-height: 0.9;
          margin: 0;
          padding: 0;
          text-shadow:
            0 0 6px rgba(0, 0, 0, 0.95),
            0 0 14px rgba(0, 0, 0, 0.8);
        }
      }
    }
  }

  .event-sections {
    background-color: white;
    width: 100%;

    > section {
      margin-left: auto;
      margin-right: auto;
      max-width: 1366px;
    }
  }

  section {
    max-width: 1366px;
    margin: auto;
    padding: 7rem 2rem;
    h2 {
      font-size: 4.2rem;
      color: white;
      font-weight: 100;
      text-align: center;
      margin: 0;

      b {
        font-weight: 700;
      }

      &.visit-heading {
        margin-top: 5rem;
      }
    }

    p,
    ul {
      font-size: 1.8rem;
      font-weight: 100;
      color: white;
    }

    .event-date {
      .event-time {
        display: inline;
        margin-left: 1.5rem;
      }

      @media only screen and (max-width: 767px) {
        font-size: min(6vw, 2.5rem);
        text-align: center;

        .event-time {
          display: block;
          margin-left: 0;
        }
      }
    }

    ul {
      list-style-type: none;
      padding: 0;
      margin: 0;
      li {
        display: flex;
        border-bottom: 1px #8e8c8c solid;
        padding: 1.8rem 0;
        div {
          flex: 1;
        }

        b {
          font-weight: 600;
        }
      }
    }

    &.event-partners {
      background-color: white;
      width: 100%;
      max-width: 1366px;
      display: flex;
      flex-direction: column;
      gap: 5rem;
      padding: 6rem 2rem;
      h2,
      p {
        color: black;
      }

      .logos {
        align-items: center;
        display: flex;
        gap: 4rem;
        justify-content: center;
        gap: 15rem;

        @media only screen and (max-width: 767px) {
          flex-direction: column;
          gap: 5rem;
        }

        > a {
          align-items: center;
          display: flex;
          flex: 0 1 25%;
          justify-content: center;
          width: 25%;

          @media only screen and (max-width: 767px) {
            width: 80%;
          }
        }

        img {
          height: auto;
          max-width: 100%;
          width: auto;

          &.ots-logo {
            height: clamp(8rem, 12vw, 14rem);
            object-fit: contain;
            width: 100%;
          }

          @media only screen and (max-width: 767px) {
            max-width: 100%;
            width: 100%;

            &.ots-logo {
              width: 100%;
            }
          }
        }
      }

      .form {
        margin: auto;
        max-width: 600px;
        width: 100%;

        form {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .form-required-note {
          margin: 0;
          text-align: left;
        }

        .form-row {
          display: flex;
          gap: 2rem;

          label {
            flex: 1 1 0;
            min-width: 0;
          }

          &.single-field {
            width: 100%;
            label {
              flex: 1 1 100%;
              width: 100%;
            }
          }
        }

        input,
        select {
          background-color: #efefef;
          border: 0;
          border-radius: 0;
          font: inherit;
          min-height: 4.5rem;
          padding: 0.75rem 1rem;
          width: 100%;
          font-size: 2rem;
          font-weight: 300;
        }

        input::placeholder {
          color: black;
          opacity: 1;
        }

        select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' fill='none' stroke='%23000' stroke-width='2'/%3E%3C/svg%3E");
          background-position: right 1rem center;
          background-repeat: no-repeat;
          background-size: 1.2rem;
          color: black;
          padding-right: 3rem;
        }

        button {
          align-self: flex-start;
          background-color: rgb(28, 24, 25);
          border: 2px solid black;
          color: white;
          cursor: pointer;
          font: inherit;
          font-size: 1.8rem;
          margin-top: 1rem;
          max-width: 300px;
          padding: 1rem 3rem;
          text-align: center;
          width: 100%;
          margin: auto;
          margin-top: 3rem;
          font-weight: 600;

          &:disabled {
            cursor: wait;
            opacity: 0.65;
          }
        }

        .submission-message {
          color: black;
          margin: 0;
          text-align: center;

          &.error {
            color: #b42318;
          }
        }

        @media only screen and (max-width: 600px) {
          .form-row {
            flex-direction: column;

            label,
            &.single-field label {
              flex-basis: 100%;
            }
          }
        }
      }
    }

    .text-gray {
      color: #8e8c8c;
    }

    .flex {
      display: flex;
      gap: 5rem;

      @media only screen and (max-width: 820px) {
        flex-direction: column;
      }

      .map {
        text-align: center;

        .text-gray {
          font-weight: 600;
        }

        @media only screen and (min-width: 820px) {
          img {
            width: 500px;
          }
        }
        p {
          font-weight: 500;
        }
      }
    }
  }
}
</style>
